import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLayout } from '../../context/LayoutContext';
import { usePersistedInterviewState } from '../../hooks/usePersistedInterviewState';
import { getWithExpiry, removeItem } from '../../utils/sessionStorage';
import Button from '../../components/Button';
import VideoCard from '../../components/interviews/VideoCard';
import { isEmptyTranscript, truncateName } from '../../utils/dataUtils';
import HLSVideoPlayer from '../../components/interviews/HLSVideoPlayer';
import { convertApiUrlToHlsUrl } from '../../utils/hlsUtils';
import { Tooltip } from '@jobtarget/ui-library';
import { MediaService } from '../../api/MediaService';
import { AnalyticsService } from '../../api/AnalyticsService';
import Spinner from '../../components/Spinner';
import Icon from '../../components/ui/Icon/Icon';
import RotatingIcon from '../../components/ui/Icon/RotatingIcon';
import Toast from '../../components/Toast';
import JSZip from 'jszip';
import config from '../../data/config';

const VideoPlayerSection: React.FC<{
  videoUrl: string;
  contentType: 'video' | 'audio';
  thumbnailUrl?: string;
  isPlaying: boolean;
  onPlayToggle: () => void;
  isMobile?: boolean;
  isMuted?: boolean;
  setIsMuted?: (muted: boolean) => void;
}> = React.memo(({ videoUrl, contentType, thumbnailUrl, isPlaying, onPlayToggle, isMobile = false, isMuted = false, setIsMuted }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div style={{
      position: 'relative',
      borderRadius: '16px',
      overflow: 'hidden',
      aspectRatio: '16/9',
      marginBottom: '1rem',
      width: '100%',
    }}>
      {/* Always show thumbnail first, only replace with HLS when playing */}
      {!isPlaying ? (
        // Show thumbnail when not playing (like before)
        <img 
          src={thumbnailUrl || '/images/blur.png'} 
          alt="Content thumbnail"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover' 
          }}
        />
      ) : (
        // Only use HLS player when actually playing
        contentType && (
          <HLSVideoPlayer
            apiUrl={videoUrl} 
            contentType={contentType}
            thumbnailUrl={thumbnailUrl}
            controls={true} // Always show controls when playing
            autoplay={true} // Auto play when switching to HLS
            muted={isMuted}
            style={{ width: '100%', height: '100%' }}
            onLoadEnd={() => console.log(`${contentType} loaded`)}
            onError={(error) => console.error(`${contentType} error:`, error)}
          />
        )
      )}

      {/* Overlays - only show when NOT playing */}
      {!isPlaying && (
        <>
          {/* Bottom Gradient Overlay */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '99.5%',
            height: '50%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
            pointerEvents: 'none',
            borderRadius: '16px',
          }} />

          {/* Play Button Overlay */}
          <div 
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              cursor: 'pointer'
            }}
            onClick={onPlayToggle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <i 
              className="fas fa-play"
              style={{
                color: 'white',
                opacity: isHovered ? 1 : 0.75,
                fontSize: isMobile ? '48px' : '96px',
                transition: 'opacity 0.2s ease'
              }}
            />
          </div>

          {/* Volume Control */}
          <div style={{
            position: 'absolute',
            bottom: '0.5rem',
            right: '0.5rem',
            padding: '0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent triggering play
            setIsMuted?.(!isMuted); // ✅ Toggle mute state
          }}
          >
            <Icon
              icon={isMuted ? "fas fa-volume-xmark" : "fas fa-volume-high"}
              style={{
                color: 'white',
                fontSize: '20px',
                opacity: 1,
                transition: 'opacity 0.2s ease'
              }}
              ariaHidden
            />
          </div>
        </>
      )}
    </div>
  );
});

// Applicant Info Component
const ApplicantInfoSection: React.FC<{
  applicantName: string;
  jobTitle: string;
  dateApplied: string;
  isMobile: boolean;
}> = ({ applicantName, jobTitle, dateApplied, isMobile }) => {
  const shouldDisplayNameTooltip = applicantName.length > 20;
  const shouldDisplayjobTitleTooltip = jobTitle.length > 32;

  return (
    <div style={{  
        // backgroundColor: 'red', 
        gap: '0.3rem',
        display: 'flex',  
        flexDirection: 'column',
        // marginBottom: '1.5rem'
        flex: 1
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          {shouldDisplayNameTooltip ? (
            <Tooltip message={truncateName(applicantName,32)} position="top">
              <h2 style={{ 
                fontSize: '1.5rem', 
                fontWeight: '500',
                // backgroundColor: 'blue',
                margin: 0,
                cursor: 'default'
              }}>
                {truncateName(applicantName, 20)}
                
              </h2>
            </Tooltip>
          ) : (
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: '500',
              // backgroundColor: 'blue',
              margin: 0,
              cursor: 'default'
            }}>
              {truncateName(applicantName, 20)}
              
            </h2>
          )}
          {/* <i className="far fa-arrow-up-right-from-square" style={{ fontSize: '1.2rem', color: '#2F86DD', cursor: 'pointer' }}></i> */}
        </div>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '0.4rem',
            cursor: 'default'
          }}>
            {shouldDisplayjobTitleTooltip ? (
              <Tooltip message={jobTitle} position="top">
                <p style={{ 
                  color: '#3F4254',
                  fontSize: '0.875rem',
                  // backgroundColor: 'green',
                  margin: 0,
                }}>
                  Applied for <span style={{ fontWeight: '600' }}>{truncateName(jobTitle, isMobile ? 50 : 32)}</span>
                </p>
              </Tooltip>
            ) : (
              <p style={{ 
                color: '#3F4254',
                fontSize: '0.875rem',
                // backgroundColor: 'green',
                margin: 0,
              }}>
                Applied for <span style={{ fontWeight: '600' }}>{truncateName(jobTitle, isMobile ? 50 : 32)}</span>
              </p>
            )}
            {/* <p style={{ 
              color: '#3F4254',
              fontSize: '0.875rem',
              // backgroundColor: 'green',
              margin: 0,
            }}>
              Applied for <span style={{ fontWeight: '600' }}>{truncateName(jobTitle, isMobile ? 50 : 32)}</span>
            </p> */}
            <p style={{ 
              color: '#9ca3af',
              fontSize: '0.875rem',
              // backgroundColor: 'green',
              margin: 0,
            }}>
              • {dateApplied}
            </p>
        </div>
      </div>
  );
  
};

// Action Buttons Component
const ActionButtonsSection: React.FC<{
  onViewApplication: () => void;
  onDownloadVideo: () => void;
  onDownloadQuestion: (index: number) => void;
  onQuestionSelect: (index: number) => void; 
  downloadingAll: boolean;
  downloadingQuestions: Set<number>;
  isRedirecting: boolean;
  selectedQuestionIndex: number;
  totalQuestions: number;
  contentType: string;
}> = ({ 
  onViewApplication, 
  onDownloadVideo, 
  onDownloadQuestion,
  onQuestionSelect,
  downloadingAll, 
  downloadingQuestions,
  isRedirecting,
  selectedQuestionIndex,
  totalQuestions,
  contentType
}) => {
  const dropdownRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // VI-725 Step E: download dropdown is React-controlled now (was a Bootstrap data-toggle dropdown;
  // the old shown.bs.dropdown listener no longer fires once Bootstrap JS is dropped).
  const [isDownloadOpen, setIsDownloadOpen] = React.useState(false);

  useEffect(() => {
    if (!isDownloadOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (dropdownRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setIsDownloadOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsDownloadOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isDownloadOpen]);

  return (
    <div style={{ 
      display: 'flex', 
      gap: '0.5rem',
      height: '100%', 
      alignItems: 'stretch' 
    }}>
      <style>{`
        #downloadDropdown + .dropdown-menu,
        #downloadDropdown ~ .dropdown-menu {
          top: 100% !important;
          bottom: auto !important;
          transform: none !important;
          margin-top: 0.125rem !important;
        }
        #downloadDropdown + .dropdown-menu.show,
        #downloadDropdown ~ .dropdown-menu.show {
          top: 100% !important;
          bottom: auto !important;
          transform: none !important;
        }
        #downloadDropdown[aria-expanded="true"] + .dropdown-menu,
        #downloadDropdown[aria-expanded="true"] ~ .dropdown-menu {
          top: 100% !important;
          bottom: auto !important;
          transform: none !important;
        }
      `}</style>
      <div className="dropdown" style={{ flex: 1, display: 'flex', position: 'relative' }}>
        {/* Main button - label area (downloads current question) */}
        <button
          className="btn btn-primary"
          type="button"
          disabled={downloadingAll || downloadingQuestions.size > 0}
          onClick={() => onDownloadQuestion(selectedQuestionIndex)}
          style={{
            flex: 1,
            height: '100%',
            minHeight: '40px',
            cursor: (downloadingAll || downloadingQuestions.size > 0) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: '1rem',
            paddingRight: '1',
            paddingTop: 0,
            paddingBottom: 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: 'none'
          }}
        >
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
          }}>
            {downloadingAll ? (
              <>
                <Spinner size="sm" color="light" />
                <span>Downloading...</span>
              </>
            ) : downloadingQuestions.has(selectedQuestionIndex) ? (
              <>
                <Spinner size="sm" color="light" />
                <span>Downloading...</span>
              </>
            ) : (
              <>
                <span>Download Question #{selectedQuestionIndex + 1}</span>
              </>
            )}
          </span>
        </button>
        
        {/* Arrow button - only this triggers dropdown */}
        <button
          ref={dropdownRef}
          className="btn btn-primary "
          type="button"
          id="downloadDropdown"
          aria-haspopup="true"
          aria-expanded={isDownloadOpen}
          onClick={() => setIsDownloadOpen((o) => !o)}
          disabled={downloadingAll || downloadingQuestions.size > 0}
          style={{
            height: '100%',
            minHeight: '40px',
            cursor: (downloadingAll || downloadingQuestions.size > 0) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: '0.75rem',
            paddingRight: '0.75rem',
            paddingTop: 0,
            paddingBottom: 0,
          borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: '1px solid rgba(255, 255, 255, 0.4)',
            minWidth: '40px'
          }}
        >
          <i className="fal fa-chevron-down" style={{ fontSize: '0.875rem' }}></i>
        </button>
        <div
          ref={menuRef}
          className={`dropdown-menu ${isDownloadOpen ? 'show' : ''}`}
          aria-labelledby="downloadDropdown"
          style={{ width: '100%', display: isDownloadOpen ? 'block' : 'none' }}
          onClick={() => setIsDownloadOpen(false)}
        >
        <button
          className="dropdown-item"
          type="button"
          onClick={() => {
            onQuestionSelect(0); // Reset to first question when downloading full video
            onDownloadVideo(); // Then download all videos
          }}
          disabled={downloadingAll}
          style={{
            cursor: downloadingAll ? 'not-allowed' : 'pointer',
            opacity: downloadingAll ? 0.6 : 1
          }}
        >
          {downloadingAll ? (
            <>
              <Spinner size="sm" />
              {' '}Downloading Full {contentType === 'video' ? 'Video' : 'Audio'}...
            </>
          ) : (
            <>
              {' '}Full {contentType === 'video' ? 'Video' : 'Audio'}
            </>
          )}
        </button>
        {Array.from({ length: totalQuestions }, (_, index) => (
          <button
            key={index}
            className="dropdown-item"
            type="button"
            onClick={() => {
              onQuestionSelect(index); // Set the selected question first
              onDownloadQuestion(index); // Then download it
            }}
            disabled={downloadingQuestions.has(index)}
            style={{
              cursor: downloadingQuestions.has(index) ? 'not-allowed' : 'pointer',
              opacity: downloadingQuestions.has(index) ? 0.6 : 1,
              fontWeight: index === selectedQuestionIndex ? '600' : '400'
            }}
          >
            {downloadingQuestions.has(index) ? (
              <>
                <Spinner size="sm" />
                {' '}Downloading Question #{index + 1}...
              </>
            ) : (
              <>
                {' '}Question #{index + 1}
              </>
            )}
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

// Navigation Controls Component
const NavigationControlsSection: React.FC<{
  currentIndex: number;
  totalCount: number;
  onPrevious: () => void;
  onNext: () => void;
  disabled?: boolean;
}> = ({ currentIndex, totalCount, onPrevious, onNext, disabled = false }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between', // Distribute items across full width
    width: '100%', // Take full width of parent
    marginBottom: '2rem'
  }}>
    <button
      onClick={onPrevious}
      disabled={disabled || currentIndex === 0} // avoid circular nav
      style={{
        background: 'transparent',
        border: 'none',
        cursor: (disabled || currentIndex === 0) ? 'not-allowed' : 'pointer',
        // padding: '0.5rem 1rem',
        fontSize: '0.875rem',
        color: (disabled || currentIndex === 0) ? '#adb5bd' : '#2F86DD',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontWeight: '400'
      }}
    >
      <i className="fal fa-chevron-left"></i>
      Prev
    </button>
  
    <span style={{
      fontSize: '0.875rem',
      textAlign: 'center',
      fontWeight: 500
    }}>
      {currentIndex + 1} of {totalCount} submission
    </span>
  
    <button
      onClick={onNext}
      disabled={disabled || currentIndex === totalCount - 1}
      style={{
        background: 'transparent',
        border: 'none',
        cursor: (disabled || currentIndex === totalCount - 1) ? 'not-allowed' : 'pointer',
        // padding: '0.5rem 1rem',
        fontSize: '0.875rem',
        color: (disabled || currentIndex === totalCount - 1) ? '#adb5bd' : '#2F86DD',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        fontWeight: '400'
      }}
    >
      Next
      <i className="fal fa-chevron-right"></i>
    </button>
  </div>
);

// Applicant Thumbnails Component
const ApplicantThumbnailsSection: React.FC<{
  videoData: any[];
  currentIndex: number;
  onApplicantSelect: (index: number) => void;
  isMobile?: boolean;
}> = ({ videoData, currentIndex, onApplicantSelect, isMobile = false }) => {
  // Calculate which 3 videos to show (current + adjacent)
  const getDisplayedVideos = () => {
    if (videoData.length <= 3) {
      return videoData.map((video, index) => ({ video, originalIndex: index }));
    }

    const displayed = [];
    
    // Always show the current one in the middle when possible
    const startIndex = Math.max(0, currentIndex - 1);
    const endIndex = Math.min(videoData.length - 1, startIndex + 2);
    
    // Adjust if we're at the end
    const actualStartIndex = endIndex === videoData.length - 1 && videoData.length >= 3 
      ? Math.max(0, videoData.length - 3) 
      : startIndex;

    for (let i = actualStartIndex; i <= Math.min(actualStartIndex + 2, videoData.length - 1); i++) {
      displayed.push({ video: videoData[i], originalIndex: i });
    }

    return displayed;
  };

  const displayedVideos = getDisplayedVideos();

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{
        display: 'flex',
        gap: isMobile ? '0.75rem' : '0.5rem',
        width: '100%', // Take full width of parent
        justifyContent: displayedVideos.length === 1 ? 'center' : 'flex-start',
        // padding: '0.5rem',
        // backgroundColor: 'red',
        borderRadius: '8px',
        // border: '1px solid #dee2e6'
      }}>
        {displayedVideos.map(({ video, originalIndex }) => (
          <div
            key={video.id}
            style={{
              flex: displayedVideos.length === 1 ? 'none' : 1,
              maxWidth: displayedVideos.length === 1 ? '300px' : 'none', // Limit single card width
              minWidth: displayedVideos.length > 1 ? '0' : 'auto',
              opacity: originalIndex === currentIndex ? 1 : 0.8,
              transform: originalIndex === currentIndex ? 'scale(1.02)' : 'scale(0.98)',
              transition: 'all 0.2s ease',
              border: originalIndex === currentIndex ? '2px solid #007bff' : '1px solid transparent',
              borderRadius: '12px'
            }}
          >
            <VideoCard 
              applicantName={video.applicantName}
              jobTitle="" 
              dateApplied={video.dateApplied}
              thumbnailUrl={video.thumbnailUrl}
              onClick={() => onApplicantSelect(originalIndex)}
              isMobile={isMobile}
              isLoading={false}
              videoId={video.id}
              compact={true} // Add this prop
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Right Sidebar Component
const RightSidebarSection: React.FC<{
  activeTab: 'questions' | 'transcript';
  onTabChange: (tab: 'questions' | 'transcript') => void;
  responses: any[];
  selectedQuestionIndex: number;
  onQuestionSelect: (index: number) => void;
  selectedResponse?: any;
  onDownloadQuestion: (index: number, response: any) => void;
  downloadingQuestions: Set<number>;
  isMobile?: boolean;
  contentType: string;
  showContent: boolean;
  setShowContent: (show: boolean) => void;
}> = ({ 
  activeTab, 
  onTabChange, 
  responses, 
  selectedQuestionIndex, 
  onQuestionSelect, 
  selectedResponse,
  onDownloadQuestion,
  downloadingQuestions,
  isMobile = false,
  contentType = 'video',
  showContent = true,
  setShowContent = () => {}
}) => {
  // Track which accordions are open
  const [openAccordions, setOpenAccordions] = useState<Set<number>>(new Set());

  const toggleAccordion = (index: number) => {
    setOpenAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setOpenAccordions(new Set());
  }, [responses]);

  console.log("RightSidebarSection - selectedResponse:", selectedResponse);

  return (
    <div style={{ 
      flex: isMobile ? 'none' : (showContent ? '1' : '0'),
      // minWidth: isMobile ? 'auto' : (showContent ? '400px' : 'auto'), 
      // maxWidth: isMobile ? 'auto' : (showContent ? 'none' : 'fit-content'),
      // width: isMobile ? 'auto' : (showContent ? 'auto' : 'fit-content'),
      borderRadius: '8px',
      height: isMobile ? 'auto' : 'fit-content',
      overflow: 'visible',
      transition: 'flex 0.3s ease, min-width 0.3s ease',
      border: '1px solid #dee2e6',
      backgroundColor: 'white',
      display: showContent ? 'block' : 'inline-block'
    }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: '#f8f9fa',
        paddingLeft: showContent ? '1rem' : '0.5rem', 
        paddingRight: showContent ? '1rem' : '0.5rem',
        paddingTop: showContent ? '0.5rem' : '0.25rem',
        paddingBottom: '0.2rem',
        borderBottom: showContent ? '1px solid #dee2e6' : 'none',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        borderBottomLeftRadius: showContent ? '0' : '8px',
        borderBottomRightRadius: showContent ? '0' : '8px',
        whiteSpace: 'nowrap',
        width: isMobile ? '100%' : (showContent ? '100%' : 'fit-content'),
        display: isMobile ? 'block' : (showContent ? 'block' : 'inline-block')
      }}>
        <button
          style={{
            background: 'none',
            border: 'none',
            fontSize: '1rem',
            fontWeight: '400',
            cursor: 'default',
            padding: '0.5rem 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : (showContent ? 'space-between' : 'center'),
            width: isMobile ? '100%' : (showContent ? '100%' : 'auto'),
            gap: '0.5rem'
          }}
        >
          <span>Questions</span>
          <Icon
            icon={showContent ? "fal fa-arrow-right-to-line" : "fal fa-arrow-left-to-line"}
            style={{
              fontSize: isMobile ? '1rem' : '1.2rem',
              color: '#6C757D',
              marginLeft: isMobile ? 'auto' : (showContent ? 'auto' : '0.5rem'),
              cursor: 'pointer',
              transition: 'transform 0.3s ease',
              flexShrink: 0
            }}
            onClick={() => setShowContent(!showContent)}
            ariaLabel="Toggle questions panel"
          />
        </button>
      </div>

      {/* Content - Always show questions */}
      {showContent && (
        <div style={{ 
          maxHeight: isMobile ? '400px' : '745px',
          overflow: 'auto',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        }}>
          <div style={{ padding: '1.3rem' }}>
            {responses && responses.length > 0 ? (
              <div className="accordion" id="questionsAccordion">
                {responses.map((response: any, index: number) => {
                  const isOpen = openAccordions.has(index);
                  const hasTranscript = !isEmptyTranscript(response?.answer?.text);
                  const accordionId = `collapse${index}`;
                  const headingId = `heading${index}`;

                  return (
                    <div 
                      key={index}
                      className="card"
                      style={{
                        marginBottom: '8px',
                        border: selectedQuestionIndex === index ? '2px solid #2F86DD' : '1px solid #dee2e6',
                        borderRadius: '8px',
                      }}
                    >
                      {/* Question Header */}
                      <div 
                        style={{
                          padding: '1rem',
                          cursor: 'pointer',
                          backgroundColor: 'transparent'
                        }}
                        onClick={() => {
                          onQuestionSelect(index);
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.75rem'
                        }}>
                          <span style={{ 
                            fontWeight: '400',
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            lineHeight: '1.4',
                            color: 'black',
                            flex: 1
                          }}>
                            {index + 1}. {response.question?.text || 'No question text available'}
                          </span>
                        </div>
                        
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          <span 
                            className="badge badge-pill"
                            style={{
                              backgroundColor: '#E3FBFA',
                              color: '#0F6E69',
                              border: 'none',
                              fontWeight: '500',
                              fontSize: '0.75rem',
                            }}
                          >
                            Answer Available
                          </span>
                        </div>
                      </div>

                      {/* Show/Hide Transcript Section */}
                      <div className="card-header" id={headingId} style={{ 
                        padding: '0',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderTop: '1px solid #ebedf3'
                      }}>
                        <h3 className="mb-0" data-toc-ignore="true">
                          <button
                            className={`btn btn-link btn-block text-left ${isOpen ? '' : 'collapsed'}`}
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={accordionId}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAccordion(index);
                            }}
                            style={{
                              width: '100%',
                              textAlign: 'left',
                              padding: '0.75rem 1rem',
                              border: 'none',
                              backgroundColor: 'transparent',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              textDecoration: 'none'
                            }}
                            onMouseDown={(e) => {
                              e.preventDefault(); // Prevents focus outline on click
                            }}
                          >
                            <span style={{ 
                              fontSize: isMobile ? '0.875rem' : '1rem', 
                              fontWeight: '500',
                              color: 'black'
                            }}>
                              {isOpen ? 'Hide Transcript' : 'Show Transcript'}
                            </span>
                            <RotatingIcon
                              icon="fal fa-chevron-down"
                              isOpen={isOpen}
                              style={{
                                fontSize: isMobile ? '1rem' : '1.2rem',
                                color: '#6C757D'
                              }}
                            />
                          </button>
                        </h3>
                      </div>

                      {/* Transcript Accordion Body - always show */}
                      <div 
                        id={accordionId}
                        className={`collapse ${isOpen ? 'show' : ''}`}
                        aria-labelledby={headingId}
                        data-parent="#questionsAccordion"
                        style={isOpen ? {} : { display: 'none' }}
                      >
                        <div className="card-body" style={{
                          padding: '1rem',
                          paddingTop: '0.5rem',
                          borderTop: 'none'
                        }}>
                          {hasTranscript ? (
                            <div style={{
                              borderRadius: '6px',
                              fontSize: isMobile ? '0.8rem' : '0.875rem',
                              lineHeight: '1.5',
                              color: 'black'
                            }}>
                              {response.answer.text}
                            </div>
                          ) : (
                            <div style={{
                              borderRadius: '6px',
                              fontSize: isMobile ? '0.8rem' : '0.875rem',
                              lineHeight: '1.5',
                              color: '#6c757d',
                              fontStyle: 'italic'
                            }}>
                              No transcriptions available
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                No questions available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const ExpandedInterviewPage = () => {
  const { 
    setTitle, 
    setIcon, 
    clearOnUnmount,
    setHeaderContent
  } = useLayout();

  const { applicantGuid } = useParams<{ applicantGuid: string }>();
  setHeaderContent(null);
  const navigate = useNavigate();
  
  const [videoData, setVideoData] = useState<any[]>([]);
  const [cachedFilters, setCachedFilters] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'cached' | 'unavailable'>('unavailable');
  
  // UI State
  const [activeTab, setActiveTab] = useState<'questions' | 'transcript'>('questions');
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number>(0);

  const [isRedirecting, setIsRedirecting] = useState(false);

  const [videoPlayerState, setVideoPlayerState] = useState({
    isPlaying: false,
    videoUrl: '',
    contentType: 'video' as 'video' | 'audio'
  });

  const [isMuted, setIsMuted] = useState(false);
  
  // Add download loading state
  const [downloadingQuestions, setDownloadingQuestions] = useState<Set<number>>(new Set());

  // Download All
  const [downloadingAll, setDownloadingAll] = useState(false);

  // Add alert state
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    message: '',
    type: 'info' as 'success' | 'error' | 'warning' | 'info',
    title: ''
  });
  
  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 835);

  const handleTabChange = (tab: 'questions' | 'transcript') => {
    setActiveTab(tab);
    setShowContent(true);
  };

  const [showContent, setShowContent] = useState(true);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 835);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data on mount
  useEffect(() => {
    const loadData = () => {
      const cachedData = getWithExpiry('expandedInterviewData');
      
      if (cachedData && cachedData.allVideos && Array.isArray(cachedData.allVideos)) {
        setVideoData(cachedData.allVideos);
        setCachedFilters(cachedData.filters);
        setDataSource('cached');
        setIsLoading(false);
        return;
      }

      setDataSource('unavailable');
      setIsLoading(false);
    };

    loadData();
  }, []);

  // Find current interview
  const currentInterviewIndex = useMemo(() => {
    if (!videoData.length) return -1;
    return videoData.findIndex(v => v.id === applicantGuid);
  }, [videoData, applicantGuid]);

  const currentInterview = currentInterviewIndex >= 0 ? videoData[currentInterviewIndex] : null;

  // Set title
  useEffect(() => {
    if (currentInterview) {
      setTitle(`${currentInterview.jobTitle}`);
      setIcon('/svgs/expanded-view-icon.svg');
    }
  }, [currentInterview?.jobTitle, setTitle, setIcon]);

  // Cleanup on unmount - removed clearOnUnmount to avoid StrictMode conflicts
  useEffect(() => {
    return () => {
      setHeaderContent(null);
    };
  }, [setHeaderContent]);

  useEffect(() => {
    // Reset video player when switching applicants
    // console.warn("RESETTING PLAYING")
    setVideoPlayerState({
      isPlaying: false,
      videoUrl: '',
      contentType: 'video'
    });
    setSelectedQuestionIndex(0); 
  }, [applicantGuid])

  useEffect(() => {
    // Stop playing when question changes (PARTIAL RESET)
    if (videoPlayerState.isPlaying) {
      setVideoPlayerState(prev => ({
        ...prev,
        isPlaying: false // Only stop playing, keep URL/contentType
      }));
    }
  }, [selectedQuestionIndex]);

  const currentResponses = currentInterview?.interview?.responses || [];

  useEffect(() => {
    // Set initial selected question based on applied question filter
    if (cachedFilters?.appliedQuestions && currentResponses.length > 0) {
      const appliedQuestionIndex = currentResponses.findIndex((response: any) => 
        response.question?.id === cachedFilters.appliedQuestions
      );
      
      if (appliedQuestionIndex >= 0) {
        console.log(`Setting selected question to applied question at index ${appliedQuestionIndex}`);
        setSelectedQuestionIndex(appliedQuestionIndex);
      } else {
        console.log('Applied question not found in current responses, keeping default (0)');
        setSelectedQuestionIndex(0);
      }
    } else {
      // No applied question filter, default to first question
      setSelectedQuestionIndex(0);
    }
  }, [cachedFilters, currentResponses.length, currentInterview?.id]);

  // Navigation functions
  const navigateToInterview = (index: number) => {
    if (index >= 0 && index < videoData.length) {
      const newInterview = videoData[index];
      navigate(`/v1/recruiter/interview/${newInterview.id}`, { replace: true });
    }
  };

  const goToPrevious = () => {
    if (currentInterviewIndex > 0) {
      const newIndex = currentInterviewIndex - 1;
      navigateToInterview(newIndex);
    }
    // Don't wrap around - stop at the beginning
  };

  const goToNext = () => {
    if (currentInterviewIndex < videoData.length - 1) {
      const newIndex = currentInterviewIndex + 1;
      navigateToInterview(newIndex);
    }
    // Don't wrap around - stop at the end
  };
  
  // Arrow key navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys if not typing in an input/textarea/select
      if (event.target instanceof HTMLInputElement || 
          event.target instanceof HTMLTextAreaElement || 
          event.target instanceof HTMLSelectElement) {
        return;
      }

      // Prevent navigation if there's only one interview
      if (videoData.length <= 1) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNext();
      }
    };

    // Add event listener to document
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToPrevious, goToNext, videoData.length]);

  // download specific question
  const handleDownloadQuestion = async (questionIndex: number, response: any) => {
    const currentContentType = currentInterview?.contentType || 'video';
    const rawUrl = currentContentType === 'video' ? 
      response.answer?.video : 
      response.answer?.audio;
      
    if (!rawUrl) {
      console.error('No media URL found for download');
      setSuccessAlert({
        show: true,
        message: 'No media URL found for download',
        type: 'info',
        title: 'No media URL found for download'
      });
      return;
    }
  
    // Set loading state
    setDownloadingQuestions(prev => new Set(prev).add(questionIndex));
  
    try {
      console.log("Starting download for:", rawUrl);

      await MediaService.downloadMedia(
        rawUrl, 
        currentContentType, 
        questionIndex, 
        currentInterview.applicantName
      );

      setSuccessAlert({
        show: true,
        message: `Question ${questionIndex + 1} for ${currentInterview.applicantName} was downloaded successfully!`,
        type: 'info',
        title: 'Download successful'
      });
      
      console.log("Download completed successfully");
    } catch (error: any) {
      console.error('Download failed:', error);
      setSuccessAlert({
        show: true,
        message: `Question ${questionIndex + 1} for ${currentInterview.applicantName} was not downloaded. Please try again.`,
        type: 'error',
        title: 'Download unsuccessful'
      });
    } finally {
      // Clear loading state
      setDownloadingQuestions(prev => {
        const newSet = new Set(prev);
        newSet.delete(questionIndex);
        return newSet;
      });
    }
  };

  // download all responses
  const handleDownloadAllVideos = async () => {
    if (!currentInterview || !currentResponses.length) {
      console.error('No interview data or responses available');
      setSuccessAlert({
        show: true,
        message: 'No videos available to download',
        type: 'info',
        title: 'No videos available to download'
      });
      return;
    }

    setDownloadingAll(true);
    const currentContentType = currentInterview.contentType || 'video';
    
    try {
      console.log(`Starting parallel download of ${currentResponses.length} ${currentContentType}s for ${currentInterview.applicantName}`);
      
      // Create array of download promises for parallel execution
      const downloadPromises = currentResponses.map((response: any, index: number) => {
        const rawUrl = currentContentType === 'video' ? 
          response.answer?.video : 
          response.answer?.audio;
          
        if (!rawUrl) {
          console.warn(`No ${currentContentType} URL found for question ${index + 1}`);
          return Promise.resolve(null);
        }

        return MediaService.downloadMediaAsBlob(
          rawUrl,
          currentContentType,
          index,
          currentInterview.applicantName
        );
      });

      // Execute all downloads in parallel
      const results = await Promise.all(downloadPromises);
      
      // Filter out failed downloads
      const successfulDownloads = results.filter(result => result !== null) as Array<{ blob: Blob, filename: string }>;
      const totalAttempts = currentResponses.length;
      
      if (successfulDownloads.length === 0) {
        setSuccessAlert({
          show: true,
          message: 'Failed to download any files. Please try again.',
          type: 'info',
          title: 'Failed to download any files'
        });
        return;
      }

      // Create ZIP file
      const zip = new JSZip();
      
      successfulDownloads.forEach(({ blob, filename }) => {
        zip.file(filename, blob);
      });

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link for ZIP
      const zipFilename = `${currentInterview.applicantName.replace(/\s+/g, '-')}-screener-${currentContentType}s.zip`;
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(zipBlob);
      downloadLink.download = zipFilename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Clean up object URL
      URL.revokeObjectURL(downloadLink.href);
      
      console.log(`ZIP download completed: ${successfulDownloads.length}/${totalAttempts} files zipped`);
      
      if (successfulDownloads.length === totalAttempts) {
        setSuccessAlert({
          show: true,
          message: `All ${totalAttempts} ${currentContentType}s for ${currentInterview.applicantName} zipped and downloaded successfully!`,
          type: 'info',
          title: 'Download successful'
        });
      } else {
        setSuccessAlert({
          show: true,
          message: `${successfulDownloads.length}/${totalAttempts} ${currentContentType}s zipped successfully. Some downloads may have failed.`,
          type: 'info',
          title: 'Download successful'
        });
      }
      
    } catch (error: any) {
      console.error('Download and zip failed:', error);
      setSuccessAlert({
        show: true,
        message: `Download failed: ${error.message}`,
        type: 'error',
        title: 'Download failed'
      });
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleViewApplication = async () => {
    console.log("View Application clicked", currentInterview?.id);
    
    try {
      setIsRedirecting(true);
      setSuccessAlert({
        show: true,
        message: 'Redirecting to Candidate Manager, please wait!',
        type: 'info',
        title: 'Redirecting...'
      });
      const response = await AnalyticsService.getApplicationFromAnalytics(currentInterview?.id);
      
      if (response.data?.applicantId) {
        const candidateManagerUrl = `${config.CANDIDATE_MANAGER_URL}/candidate-manager/details?candidateId=${response.data.applicantId}`;
        
        // Try window.open first to detect popup blocking
        const newWindow = window.open(candidateManagerUrl, '_blank');
        
        if (!newWindow) {
          // Popup blocked - show graceful message
          setSuccessAlert({
            show: true,
            message: 'Popup blocked! Please enable popups for this site to view applications in a new tab. Click "View Application" again after enabling popups.',
            type: 'info',
            title: 'Popup Blocked'
          });
          return;
        }
      }
    } catch(err: any) {
      console.error("Error in Viewing Application", err);
      
      // Fallback with applicantGuid
      const fallbackUrl = `${config.CANDIDATE_MANAGER_URL}`;
      const newWindow = window.open(fallbackUrl, '_blank');
      
      if (!newWindow) {
        setSuccessAlert({
          show: true,
          message: 'Popup blocked! Please enable popups for this site to view applications in a new tab.',
          type: 'info',
          title: 'Popup Blocked'
        });
      }
    } finally {
      console.log("finbally")
      setIsRedirecting(false);
    }
  };

  // Helper function to get the applied question text
  const getAppliedQuestionText = (): string => {
    if (!cachedFilters?.appliedQuestions || !currentResponses.length) {
      return '';
    }

    // Find the question text from the current responses that matches the applied question ID
    const appliedQuestion = currentResponses.find((response: any) => 
      response.question?.id === cachedFilters.appliedQuestions
    );

    return appliedQuestion?.question?.text || 'Applied question not found';
  };

  // Update the handlePlayToggle function around line 1109
  const handlePlayToggle = useCallback(() => {
    // Get current interview data inside the callback to avoid null reference issues
    const interview = currentInterviewIndex >= 0 ? videoData[currentInterviewIndex] : null;
    if (!interview || !interview.interview?.responses) return;
    
    if (!videoPlayerState.isPlaying) {
      const responses = interview.interview.responses;
      const currentContentType = interview.contentType || 'video';
      const rawUrl = currentContentType === 'video' ? 
        responses[selectedQuestionIndex]?.answer?.video : 
        responses[selectedQuestionIndex]?.answer?.audio;
      
      // 🔄 CHANGED: Pass the original API URL, let HLS player handle conversion and fallback
      if (rawUrl) {
        setVideoPlayerState({
          isPlaying: true,
          videoUrl: rawUrl, // Pass original URL, not converted HLS URL
          contentType: currentContentType
        });
      }
    } else {
      setVideoPlayerState(prev => ({ ...prev, isPlaying: false }));
    }
  }, [currentInterviewIndex, videoData, videoPlayerState.isPlaying, selectedQuestionIndex]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        {/* <div>Loading interview...</div> */}
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  // No data available
  if (dataSource === 'unavailable' || !currentInterview) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <h3>Screener Data Unavailable</h3>
        <p>Please navigate back and select the screener again.</p>
        <Button 
          text="← Go Back to Screeners" 
          onClick={() => navigate('/v1/recruiter')} 
          variant="outline-secondary" 
        />
      </div>
    );
  }

  // const currentResponses = currentInterview.interview?.responses || [];
  const selectedResponse = currentResponses[selectedQuestionIndex];
  
  // console.log("Debug - currentInterview:", currentInterview);
  // console.log("Debug - currentResponses:", currentResponses);
  // console.log("DEBUG - CACHED", cachedFilters);
  // console.log("Debug - selectedQuestionIndex:", selectedQuestionIndex);
  // console.log("Debug - selectedResponse:", selectedResponse);

  return (
    <div style={{ 
      minHeight: '90vh', 
      display: 'flex', 
      flexDirection: 'column',
      // backgroundColor: '#f8f9fa'
      // backgroundColor: 'red'
    }}>
      {/* Filters Header */}
      <div style={{
        padding: isMobile ? '0rem 0rem' : '0.5rem',
        // backgroundColor: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap',
        marginBottom: '0.5rem'
        // backgroundColor: 'red'
      }}>
        <button
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          // padding: '0.5rem 1rem',
          fontSize: '0.875rem',
          color: 'black',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontWeight: '400',
          marginBottom: '0.8rem'
        }}
      >
          <i className="fal fa-chevron-left" style={{ color:'#6C757D' }}></i>
            Back to Screeners
        </button>
        {/* Removing filters for back navigation */}
        {/* <span style={{ fontWeight: '600', fontSize: isMobile ? '0.875rem' : '1rem' }}>Filters:</span>
        <span style={{ 
          backgroundColor: '#e9ecef', 
          padding: '0.25rem 0.75rem', 
          borderRadius: '4px',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          color: '#6C757D'
        }}>
          <b>Date:</b> {cachedFilters?.selectedDateRange || 'Last 30 days'}
        </span> */}
        {/* Show applied question filter if it exists */}
        {/* {cachedFilters?.appliedQuestions && (
          <span style={{ 
            backgroundColor: '#e9ecef', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '4px',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            color: '#6C757D'
          }}>
            <b>Question:</b> {getAppliedQuestionText()}
          </span>
        )} */}

        {/* Show currently selected question (different from filter) - only if no question filter is applied */}
        {/* {selectedResponse?.question?.text && !cachedFilters?.appliedQuestions && (
          <span style={{ 
            backgroundColor: '#e9ecef', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '4px',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            color: '#6C757D'
          }}>
            <b>Question:</b> {selectedResponse.question.text}
          </span>
        )} */}
        {/* {selectedResponse?.question?.text && (
          <span style={{ 
            backgroundColor: '#e9ecef', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '4px',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            color: '#6C757D'
          }}>
            <b>Question:</b> {selectedResponse.question.text}
          </span>
        )} */}

        {/* Success Alert - Overlay */}
        {successAlert.show && (
          <Toast
            isOpen={successAlert.show}
            onClose={() => setSuccessAlert({ show: false, message: '', type: 'info', title: '' })}
            message={successAlert.message}
            type={successAlert.type}
            title={successAlert.title}
            duration={7000}
          />
        )}
      </div>

      {/* Main Content Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        padding: isMobile ? '0rem' : '0.5rem',
        gap: isMobile ? '1.5rem' : '1.5rem',
        // backgroundColor: 'blue',
      }}>
        {/* Left Container */}
        <div style={{ 
          flex: isMobile ? 'none' : (showContent ? '1.5' : '1'), 
          display: 'flex', 
          flexDirection: 'column',
          // backgroundColor: 'green',
          // backgroundColor: '#f8f9fa',
          borderRadius: '16px',
          // border: '1px solid blue'
        }}>
          <VideoPlayerSection
            videoUrl={videoPlayerState.videoUrl}
            contentType={videoPlayerState.contentType}
            thumbnailUrl={selectedResponse?.answer?.thumbnail || currentInterview.thumbnailUrl}
            isPlaying={videoPlayerState.isPlaying}
            onPlayToggle={handlePlayToggle} // Use the memoized function
            isMobile={isMobile}
            isMuted={isMuted}
            setIsMuted={setIsMuted}
          />

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            <ApplicantInfoSection 
              applicantName={currentInterview.applicantName}
              jobTitle={currentInterview.jobTitle}
              dateApplied={currentInterview.dateApplied}
              isMobile={isMobile}
            />

            <ActionButtonsSection 
              onViewApplication={handleViewApplication}
              onDownloadVideo={handleDownloadAllVideos}
              onDownloadQuestion={(index) => handleDownloadQuestion(index, currentResponses[index] || null)}
              onQuestionSelect={setSelectedQuestionIndex}
              downloadingAll={downloadingAll}
              downloadingQuestions={downloadingQuestions}
              isRedirecting={isRedirecting}
              selectedQuestionIndex={selectedQuestionIndex}
              totalQuestions={currentResponses.length}
              contentType={currentInterview?.contentType}
            />
          </div>

          <NavigationControlsSection 
            currentIndex={currentInterviewIndex}
            totalCount={videoData.length}
            onPrevious={goToPrevious}
            onNext={goToNext}
            disabled={videoData.length <= 1}
          />

          <ApplicantThumbnailsSection 
            videoData={videoData}
            currentIndex={currentInterviewIndex}
            onApplicantSelect={navigateToInterview}
            isMobile={isMobile}
          />
        </div>

        {/* Right Sidebar */}
        <RightSidebarSection 
          activeTab={activeTab}
          onTabChange={handleTabChange}
          responses={currentResponses}
          selectedQuestionIndex={selectedQuestionIndex}
          onQuestionSelect={setSelectedQuestionIndex}
          selectedResponse={selectedResponse}
          onDownloadQuestion={handleDownloadQuestion}
          downloadingQuestions={downloadingQuestions}
          isMobile={isMobile}
          contentType={currentInterview?.contentType}
          showContent={showContent}
          setShowContent={setShowContent}
        />
      </div>
    </div>
  );
};  

export default ExpandedInterviewPage;